import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import confetti from "canvas-confetti";
import "./PaymentResultPage.css";

/**
 * Página reutilizable para los estados de pago de Mercado Pago.
 *
 * Rutas esperadas:
 * /pago-exitoso
 * /pago-pendiente
 * /pago-fallido
 */
const PAYMENT_CONFIG = {
  success: {
    eyebrow: "Pago confirmado",
    title: "¡Pago exitoso!",
    description:
      "Hemos recibido la confirmación de tu pago. Tu solicitud ha sido registrada correctamente.",
    icon: "✓",
    modifier: "success",
    primaryLabel: "Continuar",
  },

  pending: {
    eyebrow: "Pago en proceso",
    title: "Tu pago está pendiente",
    description:
      "Mercado Pago todavía está procesando tu pago. No necesitas realizar el pago nuevamente.",
    icon: "◷",
    modifier: "pending",
    primaryLabel: "Volver al inicio",
  },

  failure: {
    eyebrow: "Pago no completado",
    title: "No pudimos procesar tu pago",
    description:
      "El pago no fue aprobado. Puedes intentarlo nuevamente o regresar al inicio.",
    icon: "!",
    modifier: "failure",
    primaryLabel: "Intentar nuevamente",
  },
};

const getPaymentStatusFromPath = (pathname) => {
  if (pathname.includes("pago-exitoso")) return "success";
  if (pathname.includes("pago-pendiente")) return "pending";
  if (pathname.includes("pago-fallido")) return "failure";

  return "success";
};

const formatPaymentId = (value) => {
  if (!value) return "No disponible";

  return String(value);
};

const formatReference = (value) => {
  if (!value) return "No disponible";

  return String(value);
};

const getStatusLabel = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();

  const labels = {
    approved: "Aprobado",
    pending: "Pendiente",
    in_process: "En proceso",
    rejected: "Rechazado",
    cancelled: "Cancelado",
    refunded: "Reembolsado",
  };

  return labels[normalizedStatus] || status || "No disponible";
};

export default function PaymentResult() {
  const location = useLocation();

  const paymentType = useMemo(
    () => getPaymentStatusFromPath(location.pathname),
    [location.pathname],
  );

  const config = PAYMENT_CONFIG[paymentType];

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const paymentData = useMemo(
    () => ({
      paymentId:
        searchParams.get("payment_id") ||
        searchParams.get("collection_id") ||
        null,

      status:
        searchParams.get("status") ||
        searchParams.get("collection_status") ||
        null,

      externalReference: searchParams.get("external_reference") || null,
      merchantOrderId: searchParams.get("merchant_order_id") || null,
      paymentType: searchParams.get("payment_type") || null,
      preferenceId: searchParams.get("preference_id") || null,
    }),
    [searchParams],
  );

  useEffect(() => {
    if (paymentType !== "success") return;

    const timer = setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 75,
        origin: {
          y: 0.65,
        },
        colors: ["#16a34a", "#22c55e", "#86efac", "#ffffff"],
      });
    }, 350);

    return () => clearTimeout(timer);
  }, [paymentType]);

  const handlePrimaryAction = () => {
    if (paymentType === "failure") {
      window.history.back();
      return;
    }

    window.location.href = "/";
  };
  return (
    <main className={`gac-payment-page gac-payment-page--${config.modifier}`}>
      <div className="gac-payment-page__background">
        <div className="gac-payment-page__background-orb gac-payment-page__background-orb--one" />
        <div className="gac-payment-page__background-orb gac-payment-page__background-orb--two" />
      </div>

      <div className="gac-payment-page__container">
        {/* Header */}
        <header className="gac-payment-header">
          <Link to="/" className="gac-payment-header__brand">
            <img
              loading="lazy"
              src="/images/logo/logo.webp"
              alt="Grupo Alianza Colombia Viajar"
              width={200}
            />
          </Link>

          <div className="gac-payment-header__secure">
            <img
              loading="lazy"
              src="/images/logo/logo-mercado-pago.svg"
              alt="Mercado pago"
              width={200}
            />
          </div>
        </header>

        {/* Main Card */}
        <section className="gac-payment-card">
          <div className="gac-payment-card__content">
            {/* Status icon */}
            <div
              className={`gac-payment-status-icon gac-payment-status-icon--${config.modifier}`}
            >
              <span>{config.icon}</span>
            </div>

            <span className="gac-payment-card__eyebrow">{config.eyebrow}</span>

            <h1 className="gac-payment-card__title">{config.title}</h1>

            <p className="gac-payment-card__description">
              {config.description}
            </p>

            {/* Transaction information */}
            <div className="gac-payment-summary">
              <div className="gac-payment-summary__header">
                <span className="gac-payment-summary__title">
                  Resumen de la transacción
                </span>

                <span
                  className={`gac-payment-summary__status gac-payment-summary__status--${config.modifier}`}
                >
                  {getStatusLabel(paymentData.status)}
                </span>
              </div>

              <div className="gac-payment-summary__rows">
                <div className="gac-payment-summary__row">
                  <span>Referencia de solicitud</span>

                  <strong>
                    {formatReference(paymentData.externalReference)}
                  </strong>
                </div>

                <div className="gac-payment-summary__row">
                  <span>ID de pago</span>

                  <strong>{formatPaymentId(paymentData.paymentId)}</strong>
                </div>

                {paymentData.merchantOrderId && (
                  <div className="gac-payment-summary__row">
                    <span>Orden de Mercado Pago</span>

                    <strong>{paymentData.merchantOrderId}</strong>
                  </div>
                )}
              </div>
            </div>

            {/* Success message */}
            {paymentType === "success" && (
              <div className="gac-payment-message gac-payment-message--success">
                <div className="gac-payment-message__icon">✓</div>

                <div>
                  <strong>¡Gracias por confiar en nosotros!</strong>

                  <p>
                    Conserva esta referencia para futuras consultas. Si tu
                    proceso requiere pasos adicionales, nuestro equipo se pondrá
                    en contacto contigo.
                  </p>
                </div>
              </div>
            )}

            {/* Pending message */}
            {paymentType === "pending" && (
              <div className="gac-payment-message gac-payment-message--pending">
                <div className="gac-payment-message__icon">⏳</div>

                <div>
                  <strong>Estamos verificando tu pago</strong>

                  <p>
                    El estado puede tardar unos momentos en actualizarse. No
                    realices un segundo pago mientras esperamos la confirmación.
                  </p>
                </div>
              </div>
            )}

            {/* Failure message */}
            {paymentType === "failure" && (
              <div className="gac-payment-message gac-payment-message--failure">
                <div className="gac-payment-message__icon">!</div>

                <div>
                  <strong>Tu solicitud no ha sido confirmada</strong>

                  <p>
                    Si el dinero fue descontado de tu cuenta, espera la
                    actualización del estado antes de realizar otro pago.
                  </p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="gac-payment-actions">
              <button
                type="button"
                className={`gac-payment-button gac-payment-button--${config.modifier}`}
                onClick={handlePrimaryAction}
              >
                {config.primaryLabel}

                <span aria-hidden="true">→</span>
              </button>

              <Link
                to="/"
                className="gac-payment-button gac-payment-button--secondary"
              >
                Volver al inicio
              </Link>
            </div>

            {/* Support */}
            <div className="gac-payment-support">
              <div className="gac-payment-support__icon">?</div>

              <div>
                <span className="gac-payment-support__title">
                  ¿Necesitas ayuda?
                </span>

                <p>
                  Ten a la mano tu referencia de solicitud para que podamos
                  ayudarte más rápido.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="gac-payment-footer">
          <span>© {new Date().getFullYear()} Grupo Alianza Colombia</span>

          <span className="gac-payment-footer__separator">•</span>

          <span>Procesamiento seguro de pagos</span>
        </footer>
      </div>
    </main>
  );
}
